// formatters.js
// Central money/date formatters driven by user preferences (see PreferencesContext).
// Pure functions only — no React, no JSX — so they're easy to test and reuse.
// Replaces the ad-hoc fmtMoney/money()/currencyFormat/.toFixed spots across the app.
import { format } from "date-fns";
import { normalizeUpdatedAtToDate } from "./dateFormat";

// Date presets offered in Settings. `value` is a date-fns format token.
export const DATE_PRESETS = [
   { value: "yyyy-MM-dd", label: "2026-01-31  (ISO)" },
   { value: "MM/dd/yyyy", label: "01/31/2026  (US)" },
   { value: "dd/MM/yyyy", label: "31/01/2026  (UK)" },
   { value: "MMM d, yyyy", label: "Jan 31, 2026" },
];

export const DEFAULT_DATE_FORMAT = "yyyy-MM-dd";

// Money options. In v1 only `negativeStyle` + `redNegatives` are user-editable;
// the rest are fixed defaults (kept here so "full control" can be exposed later
// with zero formatter changes).
export const DEFAULT_MONEY = {
   negativeStyle: "parens", // "minus" => -1,234.33   |   "parens" => (1,234.33)
   redNegatives: true, // accounting style — the app-wide default (users can change it in Settings)
   decimals: 2,
   thousands: true,
   currencySymbol: "$",
   locale: "en-CA",
};

const DASH = "—"; // shown for null / blank / non-numeric amounts (matches existing fmtMoney)

export function isFiniteNumber(v) {
   return typeof v === "number" && Number.isFinite(v);
}

/**
 * Break an amount into display parts so callers can style negatives (e.g. red).
 * opts.symbol (default true) — set false to omit the currency symbol (e.g. dense
 * report tables that show a $ only in the summary).
 * @returns {{ text:string, isNegative:boolean, isZero:boolean, isBlank:boolean }}
 */
export function moneyParts(amount, money = DEFAULT_MONEY, opts = {}) {
   const m = { ...DEFAULT_MONEY, ...(money || {}) };
   const showSymbol = opts.symbol !== false;

   let v = typeof amount === "string" && amount.trim() !== "" ? Number(amount) : amount;
   if (!isFiniteNumber(v)) return { text: DASH, isNegative: false, isZero: false, isBlank: true };
   if (Object.is(v, -0)) v = 0; // never render "-0" / "(0.00)"

   const isNegative = v < 0;
   const isZero = v === 0;
   const abs = Math.abs(v);

   let body;
   try {
      body = new Intl.NumberFormat(m.locale, {
         minimumFractionDigits: m.decimals,
         maximumFractionDigits: m.decimals,
         useGrouping: m.thousands,
      }).format(abs);
   } catch {
      body = abs.toFixed(m.decimals);
   }

   const withSymbol = showSymbol ? `${m.currencySymbol}${body}` : body;
   let text;
   if (!isNegative) text = withSymbol;
   else if (m.negativeStyle === "parens") text = `(${withSymbol})`;
   else text = `-${withSymbol}`;

   return { text, isNegative, isZero, isBlank: false };
}

/** Format an amount to a display string per the user's money prefs. */
export function formatMoney(amount, money = DEFAULT_MONEY, opts = {}) {
   return moneyParts(amount, money, opts).text;
}

/** True when this amount should be rendered in the "negative" accent color. */
export function shouldAccentNegative(amount, money = DEFAULT_MONEY) {
   const m = { ...DEFAULT_MONEY, ...(money || {}) };
   if (!m.redNegatives) return false;
   const p = moneyParts(amount, m);
   return p.isNegative && !p.isBlank;
}

/**
 * Format a date/timestamp per the user's date-format preset.
 * Accepts Date, ISO string, or Firestore Timestamp (via normalizeUpdatedAtToDate).
 */
export function formatDate(value, dateFormat = DEFAULT_DATE_FORMAT) {
   const date = normalizeUpdatedAtToDate(value);
   if (!date) return DASH;
   try {
      return format(date, dateFormat || DEFAULT_DATE_FORMAT);
   } catch {
      return format(date, DEFAULT_DATE_FORMAT);
   }
}
